//============================================================================
// Name        : Log.ts
// Purpose     : Define Lot record
// Created     : 2019-07-10
// Author      : Senitron Team
// Copyright   : Copyright 2019 Senitron Corporation. All rights reserved.
//============================================================================

import moment from "moment";
import { IStorageUnit } from "../components/manufacturing/inventory_reception/types";

class Lot {
    id: number;
    receiveQty: string;
    storageUnit: any;
    lotNumber: string;
    expirationDate: string;
    palletCount: string;
    palletCases: string;
    individualCases: string;
    customStorageUnit: string;
    hasCustomStorageUnit: boolean;
    missingExpDate: boolean;

    isValid: boolean;
    invalidMsg: string;

    constructor(lotId: number) {
        this.id                   = lotId;
        this.receiveQty           = "0";
        this.storageUnit          = {};
        this.lotNumber            = moment(new Date()).format("YYYY-MM-DD");
        this.expirationDate       = ""; //moment(new Date()).format("YYYY-MM-DD");
        this.palletCount          = "0";
        this.palletCases          = "0";
        this.individualCases      = "0";
        this.isValid              = false;
        this.hasCustomStorageUnit = false;
        this.customStorageUnit    = "";
        this.missingExpDate       = false;
    }

    loadFromLot(existingLot: Lot) {
        for (const prop in existingLot) {
            if (this.hasOwnProperty(prop)) {
                this[prop] = existingLot[prop];
            }
        }
    }

    setFromState(existingLot: any) {
        for (const prop in existingLot) {
            if (this.hasOwnProperty(prop)) {
                this[prop] = existingLot[prop];
            }
        }
    }

    getStorageUnit(storageUnits: IStorageUnit[]): IStorageUnit {
        if (!this.storageUnit) {
            return null;
        }
        return storageUnits.find(x => x.id == this.storageUnit.value);
    }

    getQty(): number {
        const _palletCount     = parseInt(this.palletCount, 10);
        const _palletCaseCount = parseInt(this.palletCases, 10);
        const _caseCount       = parseInt(this.individualCases, 10);

        const lotQty = (_palletCount * _palletCaseCount) + _caseCount;

        return lotQty;
    }

    getRecieveQtyLBS(storageUnits: IStorageUnit[]): number {
        // NOTE ac[2020-11]: Disabled calculating the qty when we're working with EA and custom units
        // because it wouldn't allow, receiving X EA with 1 box and custom SU of X.

        // const caseUnitNames = ["CS", "CS1", "EA", "EA1"];
        const caseUnitNames = ["CS", "CS1"];
        const measureUnit = storageUnits.find(st => st.id == this.storageUnit.value);

        if (measureUnit) {
            if (caseUnitNames.includes(measureUnit.name) && this.hasCustomStorageUnit) {
                return Number(this.receiveQty) * parseFloat(this.customStorageUnit);
            }
            return (Number(this.receiveQty) * parseFloat(measureUnit.measure_unit));
        }
        else {
            return Number(this.receiveQty);
        }
    }

    getQtyInPounds(storageUnits: IStorageUnit[], defaultStorageUnit: IStorageUnit): number {
        const individualUnits = ["LB", "LBS"];
        const caseUnitNames = ["CS", "CS1", "EA", "EA1"];

        if (defaultStorageUnit) {
            let seletedStorageMultiplier = 1;
            const lotStorageUnit = this.getStorageUnit(storageUnits);
            if (lotStorageUnit && !caseUnitNames.includes(lotStorageUnit.name) && individualUnits.includes(defaultStorageUnit.name)) {
                seletedStorageMultiplier = parseFloat(lotStorageUnit.measure_unit);
            }

            console.log("Multiplier: ", seletedStorageMultiplier);
            if (this.hasCustomStorageUnit) {
                return this.getQty() * parseFloat(this.customStorageUnit) * seletedStorageMultiplier;
            }
            else {
                return this.getQty() * parseFloat(defaultStorageUnit.measure_unit) * seletedStorageMultiplier;
            }
        }
        else {
            return this.getQty();
        }
    }

    getMeasureUnit(storageUnits: IStorageUnit[]): number {
        const st = storageUnits.find(x => x.name == this.storageUnit.label);
        const retVal = this.hasCustomStorageUnit ? this.customStorageUnit : st.measure_unit;
        return parseFloat(retVal);
    }

    getStorageUnitName(storageUnits: IStorageUnit[]): IStorageUnit {
        return storageUnits.find(x => x.name == this.storageUnit.label);
    }

    confirmLotValid(defaultStorageUnit: IStorageUnit) {
        let valid = this.isValid;
        valid = valid && !this.storageUnitMissing(defaultStorageUnit);
        return valid;
    }

    storageUnitMissing(defaultStorageUnit: IStorageUnit): boolean {
        if (this.storageUnit.label) {
            return this.storageUnit.label == "MISSING";
        }
        return defaultStorageUnit.name == "MISSING";
    }
}

export default Lot;
